import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Linkedin, Youtube } from 'lucide-react';
import { formatSiteAddress, formatFooterCopyrightLine } from '../lib/useSiteSettings';
import { fixAnpcHtml, anpcHtmlLooksInvalid } from '../lib/anpcDefaults';
import { BuiltinAnpcBanners } from './BuiltinAnpcBanners';

// TikTok logo SVG (not in lucide-react) — minimal monochrome path
function TikTokIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.42a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.85z" />
        </svg>
    );
}

function SocialLinks({ site }) {
    const links = [
        { url: site?.facebook_url, label: 'Facebook', Icon: Facebook },
        { url: site?.instagram_url, label: 'Instagram', Icon: Instagram },
        { url: site?.tiktok_url, label: 'TikTok', Icon: TikTokIcon },
        { url: site?.linkedin_url, label: 'LinkedIn', Icon: Linkedin },
        { url: site?.youtube_url, label: 'YouTube', Icon: Youtube },
    ].filter((l) => l.url && l.url.trim());
    if (links.length === 0) return null;
    return (
        <div className="site-public-footer__social" data-testid="footer-social-links">
            {links.map(({ url, label, Icon }) => (
                <a key={label} href={url} target="_blank" rel="noopener noreferrer"
                   aria-label={label} title={label}
                   className="site-public-footer__social-link">
                    <Icon size={18} />
                </a>
            ))}
        </div>
    );
}

export function SiteLegalBadges({ site }) {
    if (!site) return null;

    const fixedHtml = fixAnpcHtml(site.anpc_html);
    const hasValidHtml = Boolean(fixedHtml?.trim()) && !anpcHtmlLooksInvalid(fixedHtml);
    const hasAnpcImage = Boolean(site.anpc_image_url);
    const hasPayment = Boolean(site.payment_banner_url);

    let anpcBlock;
    if (hasValidHtml) {
        anpcBlock = (
            <div
                className="site-anpc-html-block"
                dangerouslySetInnerHTML={{ __html: fixedHtml }}
                data-testid="site-anpc-html"
            />
        );
    } else if (hasAnpcImage) {
        anpcBlock = (
            <a
                href={site.anpc_link || 'https://anpc.ro/ce-este-sal/'}
                target="_blank"
                rel="noopener noreferrer"
                className="site-legal-badge"
                title="ANPC — Soluționarea Alternativă a Litigiilor"
                data-testid="site-anpc-badge"
            >
                <img src={site.anpc_image_url} alt="ANPC SAL" />
            </a>
        );
    } else {
        anpcBlock = <BuiltinAnpcBanners />;
    }

    return (
        <div className="site-legal-stack" data-testid="site-legal-badges">
            {hasPayment && (
                <div className="site-legal-row site-legal-row--payment">
                    <div className="site-legal-badge site-legal-badge--payment" data-testid="site-payment-banner">
                        <img src={site.payment_banner_url} alt="Metode de plată acceptate" />
                    </div>
                </div>
            )}
            <div className="site-legal-row site-legal-row--anpc">
                {anpcBlock}
            </div>
        </div>
    );
}

export function SitePublicFooter({ site, showNav = true }) {
    const siteAddress = formatSiteAddress(site);
    const legalBits = [
        site?.cui ? `CUI ${site.cui}` : null,
        site?.trade_register ? `Reg. Com. ${site.trade_register}` : null,
    ].filter(Boolean);

    return (
        <footer className="site-public-footer" data-testid="site-public-footer">
            <div className="container-x site-public-footer__inner">
                <div className="site-public-footer__main">
                    <div className="site-public-footer__info">
                        <div>{formatFooterCopyrightLine(site)}</div>
                        {siteAddress && <div>{siteAddress}</div>}
                        {legalBits.length > 0 && <div>{legalBits.join(' · ')}</div>}
                    </div>
                    {showNav && (
                        <div className="site-public-footer__nav">
                            <Link to="/contact">Contact</Link>
                            <Link to="/login">Login</Link>
                            <Link to="/register">Înregistrare</Link>
                            <Link to="/legal/terms">Termeni</Link>
                            <Link to="/legal/privacy">Confidențialitate</Link>
                            <Link to="/legal/cookies">Cookies</Link>
                        </div>
                    )}
                    <SocialLinks site={site} />
                </div>
                <SiteLegalBadges site={site} />
            </div>
        </footer>
    );
}
